/**
 * Obstruction History Config
 *
 * Maps `ObstructionHistoryEventType` values to the icon / color / summary /
 * diff renderers consumed by the generic `OperationHistory` component.
 * Mirrors `taskHistoryConfig.ts` — driven entirely by event metadata so the
 * timeline view stays artifact-agnostic.
 */
import type { ComponentType } from 'react';
import { useMemo } from 'react';
import {
  IconAlertTriangle,
  IconBuilding,
  IconClock,
  IconEdit,
  IconPlus,
  IconShape,
  IconSignRight,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import type {
  OperationEventColor,
  OperationHistoryConfig,
  OperationHistoryEvent,
  OperationHistoryEventDiff,
} from '@/components/common/OperationHistory';
import type { ObstructionHistoryEvent } from '@/types/obstructions';
import { ObstructionHistoryEventType } from '@/types/obstructions';
import { lowerFirst } from '@/utils/helpers';

const OBSTRUCTION_EVENT_ICONS: Record<
  ObstructionHistoryEventType,
  ComponentType<{ size?: number }>
> = {
  [ObstructionHistoryEventType.Created]: IconPlus,
  [ObstructionHistoryEventType.Updated]: IconEdit,
  [ObstructionHistoryEventType.StatusChanged]: IconClock,
  [ObstructionHistoryEventType.GeometryChanged]: IconShape,
  [ObstructionHistoryEventType.SignsChanged]: IconSignRight,
  [ObstructionHistoryEventType.ContractorChanged]: IconBuilding,
  [ObstructionHistoryEventType.Deleted]: IconTrash,
};

const OBSTRUCTION_EVENT_COLORS: Record<
  ObstructionHistoryEventType,
  OperationEventColor
> = {
  [ObstructionHistoryEventType.Created]: 'green',
  [ObstructionHistoryEventType.Updated]: 'indigo',
  [ObstructionHistoryEventType.StatusChanged]: 'orange',
  [ObstructionHistoryEventType.GeometryChanged]: 'purple',
  [ObstructionHistoryEventType.SignsChanged]: 'purple',
  [ObstructionHistoryEventType.ContractorChanged]: 'indigo',
  [ObstructionHistoryEventType.Deleted]: 'red',
};

/**
 * Translation-key prefix for events that store enum values in metadata.
 * `StatusChanged` events store the raw enum string in `metadata.oldValue /
 * newValue`; we re-translate them via the shared `obstructions.status.*`
 * dictionary so the diff renders localised names.
 */
const VALUE_TRANSLATION_PREFIX: Partial<
  Record<ObstructionHistoryEventType, string>
> = {
  [ObstructionHistoryEventType.StatusChanged]: 'obstructions.status',
};

function isKnownObstructionEventType(
  type: string
): type is ObstructionHistoryEventType {
  return type in OBSTRUCTION_EVENT_ICONS;
}

export function mapObstructionEvent(
  event: ObstructionHistoryEvent
): OperationHistoryEvent {
  return {
    id: event.id,
    eventType: event.eventType,
    description: event.details ?? event.eventType,
    authorName: event.userName,
    authorId: event.userId,
    authorAvatarUrl: event.userAvatarUrl,
    createdAt: event.createdAt,
    metadata: event.metadata,
  };
}

export function useObstructionHistoryConfig(): OperationHistoryConfig {
  const { t } = useTranslation();

  return useMemo<OperationHistoryConfig>(() => {
    function translateValue(eventType: string, value: string): string {
      const prefix =
        VALUE_TRANSLATION_PREFIX[eventType as ObstructionHistoryEventType];
      if (prefix) return t(`${prefix}.${value}`, { defaultValue: value });
      return value;
    }

    function extractEventDiff(
      event: OperationHistoryEvent
    ): OperationHistoryEventDiff | null {
      if (!event.metadata) return null;
      const meta = event.metadata;

      if (meta.oldValue !== undefined && meta.newValue !== undefined) {
        return {
          from: translateValue(event.eventType, String(meta.oldValue)),
          to: translateValue(event.eventType, String(meta.newValue)),
        };
      }

      // Free-form details fallback — render as a single "to" chip
      if (typeof meta.summary === 'string' && meta.summary.length > 0) {
        return { to: meta.summary };
      }

      return null;
    }

    return {
      getEventIcon: (event) =>
        isKnownObstructionEventType(event.eventType)
          ? OBSTRUCTION_EVENT_ICONS[event.eventType]
          : IconAlertTriangle,

      getEventColor: (event) =>
        isKnownObstructionEventType(event.eventType)
          ? OBSTRUCTION_EVENT_COLORS[event.eventType]
          : 'gray',

      getEventSummary: (event) =>
        t(`obstructions.history.events.${lowerFirst(event.eventType)}`, {
          defaultValue: event.description,
        }),

      getEventDiff: extractEventDiff,

      getEventTypeLabel: (eventType) =>
        t(`obstructions.history.eventTypeLabels.${lowerFirst(eventType)}`, {
          defaultValue: eventType,
        }),

      allEventTypes: Object.values(
        ObstructionHistoryEventType
      ) as readonly string[],
    };
  }, [t]);
}
